// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

// Component imports
import { Col, Input, Row, Space, Table, Tabs, Tooltip, Typography } from 'antd';
import { SearchOutlined, HighlightOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import ActivityTypeTag from '../UI/ActivityTypeTag';
import { ChaptersourceTypeIcon } from '../Icons/ChaptersourceTypeIcon';

// Misc imports
import { useFetchActivitiesQuery } from '../../store/activities/activitiesApi';
import { useFetchAutomatedAnswersQuery } from '../../store/automatedAnswers/automatedAnswersApi';
import { ACTIVITY_TYPES, ACTIVITY_TYPES_LABELS, DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import { EXPORT_RADIO_OPTIONS, EXPORT_TABLE_ENTITIES } from '../../constants/translations';
import { flatten } from '../../utils/object/flatten';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

const _selectFromResult = (result) => {
  const dataMap = new Map();
  result?.data?.items?.forEach(item => {
    dataMap.set(item.id, item);
  });

  return {
    ...result,
    data: {
      ...result.data,
      itemsMap: dataMap
    }
  };
};

const ExportTranslationsTable = ({
  selectedRadioOption,
  updateSelectedData
}) => {

  const { orgId, tenantId } = useParams();
  const { primaryBotLanguage, currentBotLanguage } = useBotLanguage();

  // Data fetching
  const { data: activitiesData, isFetching: isFetchingActivities } = useFetchActivitiesQuery(
    { orgId, tenantId },
    {
      skip: !orgId || !tenantId || !EXPORT_TABLE_ENTITIES.FLOWS,
      selectFromResult: (result) => _selectFromResult(result)
    }
  );
  const { data: automatedAnswersData, isFetching: isFetchingAutomatedAnswers } = useFetchAutomatedAnswersQuery(
    { orgId, tenantId },
    {
      skip: !orgId || !tenantId || !EXPORT_TABLE_ENTITIES.AUTOMATED_ANSWERS,
      selectFromResult: (result) => _selectFromResult(result)
    }
  );

  // States
  const [searchValue, setSearchValue] = useState('');
  const [sortedInfo, setSortedInfo] = useState({ order: 'ascend', columnKey: 'name' });
  const [filteredInfo, setFilteredInfo] = useState({ search: '', type: [] });
  const [tableDataSource, setTableDataSource] = useState([]);
  const [activeEntityKey, setActiveEntityKey] = useState(EXPORT_TABLE_ENTITIES.FLOWS.key);
  const [selectedEntityIds, setSelectedEntityIds] = useState(
    Object.fromEntries(Object.values(EXPORT_TABLE_ENTITIES).map(tab => [tab.key, []]))
  );

  // Component Handlers
  const resetFilters = () => {
    setFilteredInfo({ search: '', type: [] });
    setSearchValue('');  // Clear the search value state
  };

  const handleTableChange = (_pagination, filters, sorter) => {
    setSortedInfo(sorter);
    setFilteredInfo(prev => ({
      ...prev,
      type: filters.name || []
    }));
  };

  const handleGlobalSearch = (value) => {
    setSearchValue(value);
    setFilteredInfo({
      ...filteredInfo,
      search: value
    });
  };

  const onTabChange = (key) => {
    setActiveEntityKey(key);
    resetFilters();
  };

  const getEntityData = (entity) => {
    switch(entity) {
      case EXPORT_TABLE_ENTITIES.FLOWS.key:
        return { ...activitiesData };
      case EXPORT_TABLE_ENTITIES.AUTOMATED_ANSWERS.key:
        return { ...automatedAnswersData };
      default:
        return { items: [], total: 0 };
    }
  };

  // useEffect to set the table data source
  useEffect(() => {
    const entityData = getEntityData(activeEntityKey);

    if(entityData) {
      const filteredEntities = (entityData?.items || []).filter(entity => {

        const flattenedEntity = flatten({
          name: entity?.name,
          nodes: entity?.content?.nodes?.map(node => ({
            label: node.label[currentBotLanguage]
          }))
        });

        const stringifiedEntity = JSON.stringify(Object.values(flattenedEntity)).toLowerCase();

        const matchesSearchFilter = stringifiedEntity.includes((filteredInfo.search || '').toLowerCase());
        const matchesTypeFilter = !filteredInfo.type.length || filteredInfo.type.includes(entity?.type);

        return matchesSearchFilter && matchesTypeFilter;
      });

      setTableDataSource(filteredEntities);
    } else {
      setTableDataSource([]);
    }
  }, [activeEntityKey, filteredInfo?.search, filteredInfo?.type, automatedAnswersData?.items, activitiesData?.items]);

  useEffect(() => {
    if (selectedRadioOption === EXPORT_RADIO_OPTIONS.SELECT_ALL.key) {
      onDropdownSelect('all');
    } else {
      onDropdownSelect('none');
    }
  }, [selectedRadioOption, automatedAnswersData?.items, activitiesData?.items]);

  useEffect(() => {
    let selectedTableData = Object.fromEntries(Object.values(EXPORT_TABLE_ENTITIES).map(tab => [tab.key, []]));

    Object.keys(selectedEntityIds).forEach(entityKey => {
      const entityData = getEntityData(entityKey);
      const selectedEntityData = selectedEntityIds[entityKey].map(id => entityData?.itemsMap?.get(id) || {});
      selectedTableData[entityKey] = selectedEntityData;
    });

    updateSelectedData(selectedTableData);
  }, [selectedEntityIds, automatedAnswersData?.items, activitiesData?.items]);

  const getEntityTypesArray = (entity) => {
    switch(entity) {
      case EXPORT_TABLE_ENTITIES.FLOWS.key:
        return [ACTIVITY_TYPES.DECISION_TREE, ACTIVITY_TYPES.LIVECHAT_FLOW, ACTIVITY_TYPES.INTERVIEW, ACTIVITY_TYPES.USER_FEEDBACK];
      case EXPORT_TABLE_ENTITIES.AUTOMATED_ANSWERS.key:
        return [ACTIVITY_TYPES.AUTOMATED_ANSWERS, ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE];
      default:
        return [];
    }
  };

  const columns = [
    {
      title: `${Object.values(EXPORT_TABLE_ENTITIES).find(tab => tab.key === activeEntityKey).tableLabel} Name`,
      key: 'name',
      sorter: (a, b) => a.name[currentBotLanguage]?.localeCompare(b.name[currentBotLanguage]),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      filters: getEntityTypesArray(activeEntityKey)?.map(status => ({
        text: <ActivityTypeTag customTag={ACTIVITY_TYPES_LABELS[status]} type={status} />,
        value: status
      })),
      filteredValue: filteredInfo.type,
      onFilter: (value, record) => record.type === value,
      filterIcon: useTableFilterIcon,
      render: record => {
        const nameToDisplay = record.name[currentBotLanguage] || record.name[primaryBotLanguage];
        return nameToDisplay ? (
          <Space>
            <ActivityTypeTag customTag={
              <Tooltip
                title={ACTIVITY_TYPES_LABELS[record.type]}
              >
                <ChaptersourceTypeIcon />
              </Tooltip>
            } type={record.type} />
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[filteredInfo.search]}
              autoEscape
              textToHighlight={nameToDisplay}
            />

            {
              // If there are other search results within the nested values, show an indicator
              !nameToDisplay?.includes(filteredInfo.search) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view in Behavior tab')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        ) : <Typography.Text type="secondary">{t('(no name)')}</Typography.Text>;
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md'],
      sorter: (a, b) => a.createdAt - b.createdAt,
      sortOrder: sortedInfo.columnKey === 'createdAt' && sortedInfo.order,
      render: createdAt => {
        if (createdAt) {
          return moment(createdAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    }
  ];

  const onRowSelectChange = (newSelectedEntities) => {
    setSelectedEntityIds({
      ...selectedEntityIds,
      [activeEntityKey]: newSelectedEntities
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedEntityIds[activeEntityKey],
    onChange: onRowSelectChange,
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => onDropdownSelect('all')
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => onDropdownSelect('none')
      }
    ]
  };

  const onDropdownSelect = key => {
    if (key === 'all') {
      const entityData = getEntityData(activeEntityKey);
      const allEntityIds = entityData?.items?.map(item => item.id) || [];
      setSelectedEntityIds({
        ...selectedEntityIds,
        [activeEntityKey]: allEntityIds
      });
    } else {
      setSelectedEntityIds({
        ...selectedEntityIds,
        [activeEntityKey]: []
      });
    }
  };

  return (
    <Fragment>
      <Row className="pt-6" gutter={[16, 16]}>
        <Col xs={16}>
          <Tabs
            className="export-translations-tab"
            defaultActiveKey={EXPORT_TABLE_ENTITIES?.FLOWS?.key}
            activeKey={activeEntityKey}
            onChange={onTabChange}
            items={[
              EXPORT_TABLE_ENTITIES?.FLOWS?.key && {
                key: EXPORT_TABLE_ENTITIES.FLOWS.key,
                label: `${EXPORT_TABLE_ENTITIES.FLOWS.tabLabel} (${activitiesData?.total || 0})`
              },
              EXPORT_TABLE_ENTITIES?.AUTOMATED_ANSWERS?.key && {
                key: EXPORT_TABLE_ENTITIES.AUTOMATED_ANSWERS.key,
                label: `${EXPORT_TABLE_ENTITIES.AUTOMATED_ANSWERS.tabLabel} (${automatedAnswersData?.total || 0})`
              }
            ]}
          />
        </Col>
        <Col xs={8}>
          <Input
            value={searchValue}
            className="float-right"
            size="middle"
            prefix={<SearchOutlined className="text-secondary" />}
            placeholder={t('Search in contents')}
            allowClear
            disabled={isFetchingActivities || isFetchingAutomatedAnswers}
            onChange={event => handleGlobalSearch(event.target.value)}
            style={{ maxWidth: '200px' }}
          />
        </Col>
        <Col xs={24}>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={tableDataSource}
            onChange={handleTableChange}
            rowKey="id"
          />
        </Col>
      </Row>
    </Fragment>

  );
};

ExportTranslationsTable.propTypes = {
  selectedRadioOption: PropTypes.string,
  updateSelectedData: PropTypes.func
};

export default ExportTranslationsTable;
